export function createAnimationLoader(importModule = url => import(url)) {
	const modules = new Map();
	const loadModule = url => {
		if (!url) return Promise.reject(new Error('Missing animation asset URL'));
		if (!modules.has(url)) {
			const pending = Promise.resolve().then(() => importModule(url)).catch(error => {
				modules.delete(url);
				throw error;
			});
			modules.set(url, pending);
		}
		return modules.get(url);
	};
	return async ({ runtimeUrl, dataUrl }) => {
		const [runtime, data] = await Promise.all([loadModule(runtimeUrl), loadModule(dataUrl)]);
		if (typeof runtime.default?.loadAnimation !== 'function' || !data.default) {
			throw new Error('Invalid animation asset');
		}
		return { player: runtime.default, data: JSON.parse(JSON.stringify(data.default)) };
	};
}

const loadAnimationAssets = createAnimationLoader();

export default class DecorativeAnimation {
	constructor(container, { loop = false, preserveFallback = false, load = loadAnimationAssets, onError = () => {} } = {}) {
		this.container = container;
		this.loop = loop;
		this.preserveFallback = preserveFallback;
		this.load = load;
		this.onError = onError;
		this.generation = 0;
		this.active = false;
		this.pending = false;
		this.ready = false;
	}

	async play() {
		this.active = true;
		if (!this.container.isConnected) return;
		if (this.animation) {
			if (this.ready) this.animation.play();
			return;
		}
		if (this.pending) return;
		this.pending = true;
		const generation = ++this.generation;
		try {
			const { player, data } = await this.load({
				runtimeUrl: this.container.dataset.animationRuntimeUrl,
				dataUrl: this.container.dataset.animationDataUrl,
			});
			if (generation !== this.generation || !this.active || !this.container.isConnected) return;
			this.fallback = this.preserveFallback ? this.container.querySelector('svg') : null;
			this.fallbackVisibility = this.fallback?.style.visibility || '';
			this.layer = this.container.ownerDocument.createElement('div');
			this.layer.hidden = true;
			this.layer.style.width = '100%';
			if (this.preserveFallback) {
				this.layer.style.position = 'absolute';
				this.layer.style.inset = '0';
			}
			this.container.appendChild(this.layer);
			this.animation = player.loadAnimation({
				container: this.layer,
				renderer: 'svg',
				loop: this.loop,
				autoplay: false,
				animationData: data,
			});
			const onReady = () => {
				if (generation !== this.generation) return;
				if (!this.container.isConnected) return this.destroy();
				this.ready = true;
				if (this.fallback) this.fallback.style.visibility = 'hidden';
				this.layer.hidden = false;
				if (this.active) this.animation.play();
			};
			this.animation.addEventListener('DOMLoaded', onReady);
			this.animation.addEventListener('data_failed', () => {
				if (generation === this.generation) this.destroy();
			});
			if (this.animation.isLoaded) onReady();
		} catch (error) {
			if (generation === this.generation) {
				this.destroy();
				this.onError(error);
			}
		} finally {
			if (generation === this.generation) this.pending = false;
		}
	}

	pause() {
		this.active = false;
		if (!this.ready) {
			this.destroy();
		} else {
			this.animation?.pause();
		}
	}

	destroy() {
		this.active = false;
		this.generation += 1;
		this.pending = false;
		this.ready = false;
		this.animation?.destroy();
		this.animation = null;
		this.layer?.remove();
		this.layer = null;
		if (this.fallback) this.fallback.style.visibility = this.fallbackVisibility;
	}
}
